import { normalizeCatalogGenre, normalizeCatalogLanguage } from "@lorevok/domain";

/**
 * The catalog's language and genre filters. They live in the catalog's address
 * (`/catalog?lang=ru&genre=horror`) so a filtered catalog is linkable, and App
 * owns them (URL sync, Back/Forward). Values are kept normalized the way the
 * API compares them (the domain's catalogFilters.ts): a lowercase primary
 * language subtag, and a genre KEY — a known genre's id whatever spelling the
 * address used ("Ужасы", "Horror" → "horror"; "Dark Fantasy" → "dark-fantasy"),
 * else the trimmed lowercase text. Null = no filter.
 */
export interface CatalogFilters {
  language: string | null;
  genre: string | null;
}

export const NO_CATALOG_FILTERS: CatalogFilters = { language: null, genre: null };

/** The address bar's parameter names (the API's are `language` / `genre`). */
export const CATALOG_LANGUAGE_PARAM = "lang";
export const CATALOG_GENRE_PARAM = "genre";

/** The filters a query string (`?lang=ru&genre=horror`) names; a malformed value is ignored. */
export function parseCatalogFilters(search: string): CatalogFilters {
  const params = new URLSearchParams(search);
  return {
    language: normalizeCatalogLanguage(params.get(CATALOG_LANGUAGE_PARAM)),
    genre: normalizeCatalogGenre(params.get(CATALOG_GENRE_PARAM))
  };
}

export function hasCatalogFilters(filters: CatalogFilters): boolean {
  return filters.language !== null || filters.genre !== null;
}

export function sameCatalogFilters(a: CatalogFilters, b: CatalogFilters): boolean {
  return a.language === b.language && a.genre === b.genre;
}

/** The catalog address's query for `filters` ("" for none), e.g. "?lang=ru&genre=dark%20fantasy". */
export function catalogFilterSearch(filters: CatalogFilters): string {
  const pairs: string[] = [];
  if (filters.language) pairs.push(`${CATALOG_LANGUAGE_PARAM}=${encodeURIComponent(filters.language)}`);
  if (filters.genre) pairs.push(`${CATALOG_GENRE_PARAM}=${encodeURIComponent(filters.genre)}`);
  return pairs.length > 0 ? `?${pairs.join("&")}` : "";
}

/**
 * The API query parameters for `filters`, each led by "&" ("" for none) —
 * appended after `limit` / `offset` in apiClient's catalog URL. prefetchRoutes
 * builds the same string on its own (it may not import); its test pins them.
 */
export function catalogFilterApiParams(filters: CatalogFilters): string {
  return (
    (filters.language ? `&language=${encodeURIComponent(filters.language)}` : "") +
    (filters.genre ? `&genre=${encodeURIComponent(filters.genre)}` : "")
  );
}

/** `search` appended to a path that may already carry a query. */
export function withSearch(path: string, search: string): string {
  if (!search) return path;
  return path.includes("?") ? `${path}&${search.replace(/^\?/, "")}` : `${path}${search}`;
}
